<template>
    <div>
        <template v-for="(system, index) in $v.injectedConfig.$each.$iter">
            <System :key="index" v-slot="{ startMppt, mpptsPerDiagram , flags }" :index="index" :system="system" :full-width="true" class="hide-flags">
                <SystemDiagramString
                    :system="system"
                    :building-type="job.details.buildingType"
                    :temperature="job.details.temperature"
                    :start-mppt="startMppt"
                    :mppts-per-diagram="mpptsPerDiagram"
                    :full-width="true"
                    :calc-mode="true"
                    :flags="flags"
                >
                    <template v-slot="{ activeMppt }">
                        <div :key="activeMppt.$model.id" class="account__container py-none">
                            <div class="spe-tools account__content--small">
                                <div class="spe-tools__item mb-xl">
                                    <h3 class="h6 text--white mb-lg">Azimuth</h3>
                                    <Azimuth v-model="activeMppt.$model.spe.azimuth" />
                                </div>
                                <div class="spe-tools__item mb-xl">
                                    <h3 class="h6 text--white mb-lg">Tilt</h3>
                                    <Tilt v-model="activeMppt.$model.spe.tilt" />
                                </div>
                            </div>

                            <h3 class="h6 text--white mt-xl mb-lg">Monthly Shading Losses (%)</h3>
                            <div class="spe-losses account__content--small cf">
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.jan" type="number" placeholder="%" label="Jan" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.feb" type="number" placeholder="%" label="Feb" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.mar" type="number" placeholder="%" label="Mar" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.apr" type="number" placeholder="%" label="Apr" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.may" type="number" placeholder="%" label="May" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.jun" type="number" placeholder="%" label="Jun" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.jul" type="number" placeholder="%" label="Jul" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.aug" type="number" placeholder="%" label="Aug" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.sep" type="number" placeholder="%" label="Sep" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.oct" type="number" placeholder="%" label="Oct" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.nov" type="number" placeholder="%" label="Nov" class="white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.shading.dec" type="number" placeholder="%" label="Dec" class="white-theme" />

                                <FieldInput v-model="activeMppt.$model.spe.losses.soiling" type="number" placeholder="%" label="Soiling Losses (%)" class="clear-both double-field white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.dc" type="number" placeholder="%" label="DC Losses (%)" class="double-field white-theme" />
                                <FieldInput v-model="activeMppt.$model.spe.losses.ac" type="number" placeholder="%" label="AC Losses (%)" class="double-field white-theme" />
                            </div>

                            <p v-if="activeMppt.$dirty && activeMppt.$invalid" class="small semi text--white">Please fill in all fields above with a value between 0 and 100</p>
                        </div>
                    </template>
                </SystemDiagramString>
            </System>
        </template>

        <div class="submit account__container pt-none mt-xl">
            <button class="btn" @click="submitForm">Submit</button>
            <div v-if="loading" class="loading-state">
                <img src="/dist/images/loader.gif" alt="Loading">
                <span class="small semi text--orange">{{ loadingText }}</span>
            </div>
            <div v-else-if="error">
                <p class="small semi text--orange ml-lg">{{ error }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { map, get } from 'lodash';
import { validationMixin } from 'vuelidate';
// import { required, numeric, minValue, maxValue } from 'vuelidate/lib/validators';
import { required } from 'vuelidate/lib/validators';
import System from "@/jobs/new/System";
import SystemDiagramString from "@/jobs/new/SystemDiagramString";
import Azimuth from "@/jobs/calculators/system-performance/Azimuth";
import Tilt from "@/jobs/calculators/system-performance/Tilt";
import FieldInput from '@/form/Input';
import { axios } from '@/../utils';
import { MPPT_TEMPLATE } from '@/jobs/store/configuration';

export default {
    components: {
        System,
        SystemDiagramString,
        Tilt,
        Azimuth,
        FieldInput
    },

    mixins: [validationMixin],

    data() {
        return {
            job: JSON.parse(this.$root.job),
            products: JSON.parse(this.$root.products),
            configuration: JSON.parse(this.$root.configuration),
            loading: false,
            loadingText: null,
            error: null,
        };
    },

    computed: {
        // Basic config injected with data from product lookup data
        injectedConfig() {
            return map(this.configuration, (system) => {
                const inverter = this.products.inverters.inverters.find((i) => i.id == system.id);
                const mpptMax = Number(get(inverter, 'details.numberOfMPPTrackers', 1));
                const mppts = Array(mpptMax).fill().map((i, index) => {
                    const foundMppt = system.mppts.find((mppt) => mppt.id == index + 1);
                    return foundMppt ? {
                        ...foundMppt,
                        panel: this.products.pvModules.panels.find((prod) => prod.id == foundMppt.panel.id),
                        spe: Object.assign({}, foundMppt.spe, { losses: this.dtoi(foundMppt.spe.losses) })
                    } : MPPT_TEMPLATE(index);
                });

                return {
                    ...system,
                    inverter,
                    mppts
                };
            });
        },
        // Output needs to convert % values to decimals
        output() {
            return this.injectedConfig.map((system) => {
                const mppts = system.mppts.reduce((filtered, mppt) => {
                    if (mppt.panel) {
                        filtered.push({
                            id: mppt.id,
                            spe: Object.assign({}, mppt.spe, { losses: this.itod(mppt.spe.losses) })
                        });
                    }

                    return filtered;
                }, []);

                return {
                    id: system.id,
                    mppts
                };
            });
        }
    },

    created() {

        // GTM start completing the SPE
        if ('dataLayer' in window && !this.job.details.datasheet) {
            window.dataLayer.push({
                event: 'speComplete',
                step: 'start'
            });
        }

        // Inject new SPE shape in each MPPT
        this.configuration = map(this.configuration, (system) => {
            // If data is to be received from Craft then do a check before creating new shape
            const mppts = map(system.mppts, (mppt) => {
                if (Object.prototype.hasOwnProperty.call(mppt, 'spe')) return mppt;

                return {
                    ...mppt,
                    spe: {
                        azimuth: 0,
                        tilt: 0,
                        losses: {
                            shading: {
                                jan: 0.05,
                                feb: 0.05,
                                mar: 0.05,
                                apr: 0.05,
                                may: 0.05,
                                jun: 0.05,
                                jul: 0.05,
                                aug: 0.05,
                                sep: 0.05,
                                oct: 0.05,
                                nov: 0.05,
                                dec: 0.05,
                            },
                            soiling: 0.05,
                            dc: 0.02,
                            ac: 0.01
                        }
                    },
                };
            });

            return {
                ...system,
                mppts
            };
        });
    },

    validations: {
        injectedConfig: {
            $each: {
                inverter: {
                    required
                },
                mppts: {
                    $each: {
                        // spe: {
                        //     azimuth: {
                        //         required
                        //     },
                        //     tilt: {
                        //         required
                        //     },
                        //     losses: {
                        //         shading: {
                        //             jan: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             feb: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             mar: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             apr: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             may: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             jun: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             jul: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             aug: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             sep: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             oct: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             nov: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //             dec: {
                        //                 required,
                        //                 numeric,
                        //                 minValue: minValue(0),
                        //                 maxValue: maxValue(100)
                        //             },
                        //         },
                        //         soiling: {
                        //             required,
                        //             numeric,
                        //             minValue: minValue(0),
                        //             maxValue: maxValue(100)
                        //         },
                        //         dc: {
                        //             required,
                        //             numeric,
                        //             minValue: minValue(0),
                        //             maxValue: maxValue(100)
                        //         },
                        //         ac: {
                        //             required,
                        //             numeric,
                        //             minValue: minValue(0),
                        //             maxValue: maxValue(100)
                        //         }
                        //     }
                        // },
                    }
                }
            }
        }
    },

    methods: {
        submitForm() {
            this.$v.$touch();

            if (!this.$v.$invalid) {
                this.loadingText = 'Finding weather data...';
                this.loading = true;

                setTimeout(() => {
                    this.loadingText = 'Calculating...';
                }, 5000 * this.configuration.length);

                setTimeout(() => {
                    this.loadingText = 'Generating graphs...';
                }, 10000 * this.configuration.length);

                setTimeout(() => {
                    this.loadingText = 'Finalising...';
                }, 15000 * this.configuration.length);

                return axios.post(`/account/job/${this.job.id}/SystemPerformanceEstimator/datasheet`, {
                    job: this.id,
                    recalculate: true,
                    configuration: this.output,
                }).then((response) => {
                    if (response.data.success) {

                        // GTM finish completing the SPE
                        if ('dataLayer' in window && !this.job.details.datasheet) {
                            window.dataLayer.push({
                                event: 'speComplete',
                                step: 'complete'
                            });
                        }

                        window.location = `/account/job/${this.job.id}/SystemPerformanceEstimator/calculate`

                    } else {
                        window.location = `/account/job/${this.job.id}?error=${response.data.error}`
                    }
                }).catch(() => {
                    this.loading = false;
                    this.error = 'A connection issues has occured. Please try again later.'
                });
            }
        },

        // Convert integer values in and object to decimals
        itod(obj) {
            const result = {};
            Object.keys(obj).forEach((key) => {
                if(typeof obj[key] === 'object') {
                    result[key] = this.itod(obj[key]);
                } else {
                    result[key] = Number(obj[key]) / 100;
                }
            });
            return result;
        },

        // Convert decimal values in and object to integers
        dtoi(obj) {
            const result = {};
            Object.keys(obj).forEach((key) => {
                if(typeof obj[key] === 'object') {
                    result[key] = this.dtoi(obj[key]);
                } else {
                    result[key] = Number(obj[key]) * 100;
                }
            });
            return result;
        }
    }
};
</script>

<style lang="scss" scoped>
    .spe-tools {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        @media screen and (max-width: 650px) {
            justify-content: center;
        }
    }

    .spe-tools__item {
        text-align: center;
        flex: 0 1 285px;
    }

    .spe-losses {
        @supports (display:grid) {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(55px, 1fr));
            grid-column-gap: 58px;
            grid-row-gap: 40px;
        }

        > div {
            float: left;
            width: 55px;
            margin: 0 40px 55px 0;

            @supports (display:grid) {
                float: none;
                width: auto;
                margin: 0;
            }
        }

        .double-field {
            width: 150px;

            @supports (display:grid) {
                width: auto;
                grid-column: span 2;
            }
        }
    }

    .clear-both {
        clear: both;
    }

    .submit {
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        .btn {
            margin: 5px;
        }
    }

    .loading-state {
        display: flex;
        align-items: center;
        margin: 0.5em 1em;

        img {
            margin-right: 1em;
        }
    }
</style>
